import { useApolloClient } from '@apollo/client'

import { useUserContext } from '@/contexts/UserContext.tsx'
import { addCacheListElement, removeCacheListElement } from '@/requests/apollo/cache.ts'
import { getMutation } from '@/requests/apollo/mutation.ts'
import { LABEL_COMMAND_META } from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useQueryProjectMeta, useQueryStories } from '@/requests/queries/useQueryProject.ts'

export const useMutateLabel = () => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { projectMeta } = useQueryProjectMeta()
  const { cache } = useApolloClient()
  const { stories: doneStories } = useQueryStories('done')
  const { stories: relevantStories } = useQueryStories('relevant')
  const { stories: unscheduledStories } = useQueryStories('unscheduled')
  const stories = [...doneStories, ...relevantStories, ...unscheduledStories]

  const [execute] = getMutation('Label', LABEL_COMMAND_META)

  return {
    create: (label: Label) => {
      const requestedEntity = { ...label, __typename: 'Label' }

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        'LABEL_CREATE',
        user.id,
        label,
        requestedEntity,
      )

      return execute({
        variables,
        optimisticResponse,
        update: (cache, { data: { executeCommand } }) => {
          for (const createdEntity of executeCommand.data) {
            addCacheListElement(cache.identify({ __typename: 'Project', id: projectMeta.id }), createdEntity, 'labels')
          }
        },
      })
    },
    update: (label: Label) => {
      if (!label.id) return Promise.reject(new Error(`Label with id ${label.id} not found`))

      const { variables, optimisticResponse } = getExecuteTemplate(projectMeta, 'LABEL_UPDATE', user.id, label, {
        __typename: 'Label',
        ...label,
      })

      return execute({
        variables,
        optimisticResponse,
      })
    },
    remove: (label: Label) => {
      for (const story of stories) {
        removeCacheListElement(cache.identify(story), 'labels', label.id)
      }
      removeCacheListElement(cache.identify({ __typename: 'Project', id: projectMeta.id }), 'labels', label.id)

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        'LABEL_DELETE',
        user.id,
        {
          id: label.id,
        },
        label,
      )

      return execute({
        variables,
        optimisticResponse,
        update: (cache) => {
          cache.evict({ id: cache.identify(label) })
        },
      })
    },
  }
}
